<template>
  <div class="main-collection__nosto-grid">
    <nosto-grid
      class="main-collection__nosto"
      :in-grid-content-banner="inGridContentBanner"
      :liquid="liquid"
      :products-per-page="productsPerPage"
      :seo-title="seoTitle"
      :sort-by-options="sortByOptions"
      :storage-key="storageKey"
      :filter-family="filterFamily?.facet_name"
      :facet-exclusions="facetExclusions"
      @page-updated="handlePageUpdated"
    />
  </div>
</template>

<script>

/**
 * Vue: Main collection (main-collection)
 * -----------------------------------------------------------------------------
 * Main collection page component.
 *
 * @param {Array} facetExclusions - Facets to exclude.
 * @param {Object} filterFamily - Filter family.
 * @param {Object} inGridContentBanner - In-grid content banner metafields.
 * @param {Object} liquid - Liquid data.
 * @param {Number} productsPerPage - Products per page based on in-grid content
 * banner width.
 * @param {Object} section - Section settings.
 * @param {String} seoTitle - Title for SEO purposes.
 * @param {Object} sortByOptions - Sort by options.
 */
import { mapState } from 'vuex'

import NostoGrid from '~async/nosto-grid/nosto-grid'

export default {
  name: 'MainCollection',

  components: {
    NostoGrid,
  },

  props: {
    facetExclusions: {
      type: Array,
      default: () => [],
    },
    filterFamily: {
      type: Object,
      default: () => ({}),
    },
    inGridContentBanner: {
      type: Object,
      default: () => ({
        bannerImage: '',
        bannerLink: '',
        bannerText: '',
        bannerVideo: '',
        bannerWidth: '',
      }),
    },
    liquid: {
      type: Object,
      default: () => ({
        description: '',
        handle: '',
        image: '',
        title: '',
        url: '',
      }),
    },
    productsPerPage: {
      type: Number,
      default: 16,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
      }),
    },
    seoTitle: {
      type: String,
      default: '',
    },
    sortByOptions: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      fullDescription: false,
      selectors: {},
      storageKey: 'collection-temp',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      choices: (state) => state['main-collection'].choices,
    }),
  },

  mounted() {
    const section = document.querySelector(`[js-collection-description="section"]#${this.section.id}`)

    if (!section) {
      return
    }

    this.selectors = {
      container: section.querySelector('[js-collection-description="container"]'),
      description: section.querySelector('[js-collection-description="description"]'),
      toggle: section.querySelector('[js-collection-description="toggle"]'),
      toggleLabel: section.querySelector('[js-collection-description="toggle"] .button__label'),
      truncated: section.querySelector('[js-collection-description="truncated"]'),
    }

    if (!this.selectors.toggle) {
      return
    }

    this.selectors.toggle.addEventListener('click', this.toggleDescription)
  },

  methods: {

    /**
     * Toggle description.
     */
    toggleDescription() {
      if (this.fullDescription) {
        this.selectors.truncated.classList.remove(this.$class('hidden'))
        this.selectors.truncated.removeAttribute('aria-hidden')

        this.selectors.description.classList.add(this.$class('visuallyHidden'))
        this.selectors.description.setAttribute('aria-hidden', true)

        this.selectors.toggleLabel.innerText = this.$string('accessibility.show_more')
        this.fullDescription = false
        return
      }

      this.selectors.truncated.classList.add(this.$class('hidden'))
      this.selectors.truncated.setAttribute('aria-hidden', true)

      this.selectors.description.classList.remove(this.$class('visuallyHidden'))
      this.selectors.description.removeAttribute('aria-hidden')

      this.selectors.toggleLabel.innerText = this.$string('accessibility.show_less')
      this.fullDescription = true
    },

    /**
     * Handle Nosto update.
     * @param {Number} page - Page number.
     */
    handlePageUpdated(page) {
      if (!this.selectors.container) {
        return
      }

      if (page === 1) {
        this.selectors.container.classList.remove(this.$class('hidden'))
        return
      }

      this.selectors.container.classList.add(this.$class('hidden'))
    },
  },
}
</script>

<style lang="scss">
@import './main-collection';
@import '~async/breadcrumbs/breadcrumbs';
@import '~async/nosto-grid/nosto-grid';
@import '~async/product-card/product-card';
@import '~async/nosto-grid/nosto-filter';
@import '~async/nosto-grid/nosto-pagination';
@import '~async/in-grid-content-banner/in-grid-content-banner';
@import '~async/nosto-drawer/nosto-drawer';
@import '~async/nosto-horizontal-filter/nosto-horizontal-filter';
@import '~async/nosto-drawer/nosto-facet';
@import '~async/nosto-grid/nosto-active-values';
@import '~async/nosto-grid/nosto-active-value-tag';
</style>
